const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** Value for an `<input type="file">` accepting instruction photos. */
export const INSTRUCTION_PHOTO_ACCEPT = ALLOWED_TYPES.join(",");
export const MAX_INSTRUCTION_PHOTOS = 3;
export const MAX_INSTRUCTION_PHOTO_BYTES = 5 * 1024 * 1024;
const MAX_INSTRUCTION_TOTAL_BYTES = 10 * 1024 * 1024;

export type InstructionPhotoError = "type" | "size" | "count" | "total";

export interface InstructionPhotoDraft {
  file: File;
  previewUrl: string;
}

export interface AttachedInstructionPhoto {
  id: bigint;
  url: string;
}

export interface AcceptResult {
  accepted: InstructionPhotoDraft[];
  error: InstructionPhotoError | null;
}

export function acceptInstructionPhotos(
  held: InstructionPhotoDraft[],
  picked: File[],
  heldCount = held.length,
): AcceptResult {
  const accepted: InstructionPhotoDraft[] = [];
  let total = held.reduce((sum, photo) => sum + photo.file.size, 0);
  let error: InstructionPhotoError | null = null;

  for (const file of picked) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      error = "type";
      continue;
    }
    if (file.size > MAX_INSTRUCTION_PHOTO_BYTES) {
      error = "size";
      continue;
    }
    if (heldCount + accepted.length >= MAX_INSTRUCTION_PHOTOS) {
      error = "count";
      break;
    }
    if (total + file.size > MAX_INSTRUCTION_TOTAL_BYTES) {
      error = "total";
      break;
    }
    total += file.size;
    accepted.push({ file, previewUrl: URL.createObjectURL(file) });
  }

  return { accepted, error };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Encodes one picked photo into the proto `File` shape the upload RPCs take. */
export async function encodePhotoFile(file: File) {
  return {
    bytes: await fileToBase64(file),
    name: file.name.replace(/\.[^.]+$/, ""),
    format: file.name.split(".").pop()?.toLowerCase() ?? "jpg",
  };
}

export async function encodeInstructionPhotos(photos: InstructionPhotoDraft[]) {
  return Promise.all(photos.map(({ file }) => encodePhotoFile(file)));
}

/** Frees the preview URLs of drafts being discarded. */
export function revokeInstructionPhotos(photos: InstructionPhotoDraft[]) {
  photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
}

// Reading instruction photos off a job lives in the orders adapters, alongside the
// rest of the "which API shape is this" handling — see pickInstructionPhotos there.
